import { FC, useCallback, useMemo, useState } from 'react';
import {
  Button,
  Form,
  Input,
  InputNumber,
  Modal,
  Row,
  Select,
  Space,
  Switch,
  Table,
  Tag,
  Tabs,
  Typography,
  message
} from 'antd';
import type { ColumnsType } from 'antd/es/table';
import { ExclamationCircleOutlined } from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import { NAMESPACE, DELETE_ACTION_COLOR } from '@/constants';
import {
  TradingDeal,
  TradingDealChannel,
  useCreateAdminTradingDealChannelMutation,
  useCreateAdminTradingDealMutation,
  useDeleteAdminTradingDealChannelMutation,
  useDeleteAdminTradingDealMutation,
  useGetAdminChannelsQuery,
  useGetTradingDealChannelsQuery,
  useGetTradingDealsQuery,
  useUpdateAdminTradingDealChannelMutation,
  useUpdateAdminTradingDealMutation
} from '@/api';
import { formatPercentInput, parseNumericInput, type NumericInputValue } from '@/utils/inputNumber';

const { confirm } = Modal;

const getApiErrorMessage = (err: unknown): string => {
  if (err && typeof err === 'object' && 'data' in err) {
    return (err as { data?: { message?: string } }).data?.message ?? 'Something went wrong';
  }
  return 'Something went wrong';
};

const BuyingPointsTab: FC = () => {
  const { t } = useTranslation(NAMESPACE.COMMON);
  const [form] = Form.useForm<{
    name: string;
    percentage: number;
    isActive?: boolean;
    requiresClientOrder?: boolean;
  }>();
  const [modalOpen, setModalOpen] = useState(false);
  const [editingDeal, setEditingDeal] = useState<TradingDeal | null>(null);

  const { data, isLoading } = useGetTradingDealsQuery({ includeInactive: true });
  const [createDeal, { isLoading: isCreating }] = useCreateAdminTradingDealMutation();
  const [updateDeal, { isLoading: isUpdating }] = useUpdateAdminTradingDealMutation();
  const [deleteDeal, { isLoading: isDeleting }] = useDeleteAdminTradingDealMutation();

  const openAddModal = useCallback(() => {
    setEditingDeal(null);
    form.resetFields();
    setModalOpen(true);
  }, [form]);

  const openEditModal = useCallback(
    (record: TradingDeal) => {
      setEditingDeal(record);
      form.setFieldsValue({
        name: record.name,
        percentage: record.percentage,
        isActive: record.isActive
      });
      setModalOpen(true);
    },
    [form]
  );

  const closeModal = useCallback(() => {
    setModalOpen(false);
    setEditingDeal(null);
    form.resetFields();
  }, [form]);

  const handleSubmit = useCallback(async () => {
    try {
      const values = await form.validateFields();
      if (editingDeal) {
        await updateDeal({
          id: editingDeal._id,
          body: {
            name: values.name,
            percentage: values.percentage,
            ...(values.isActive !== undefined && { isActive: values.isActive }),
            ...(values.requiresClientOrder !== undefined && {
              requiresClientOrder: values.requiresClientOrder
            })
          }
        }).unwrap();
        message.success(t('tradingDealHasBeenEdited'));
      } else {
        await createDeal({
          name: values.name,
          percentage: values.percentage,
          requiresClientOrder: values.requiresClientOrder ?? false
        }).unwrap();
        message.success(t('tradingDealHasBeenCreated'));
      }
      closeModal();
    } catch (err) {
      message.error(getApiErrorMessage(err));
    }
  }, [closeModal, createDeal, editingDeal, form, t, updateDeal]);

  const handleDelete = useCallback(
    (record: TradingDeal) => {
      confirm({
        title: <span style={{ color: DELETE_ACTION_COLOR }}>{t('deleteTradingDealModalTitle')}</span>,
        content: t('deleteTradingDealModalDescription'),
        icon: <ExclamationCircleOutlined />,
        okText: t('cancel'),
        cancelText: t('delete'),
        cancelButtonProps: { danger: true, style: { color: DELETE_ACTION_COLOR } },
        onCancel: () =>
          deleteDeal(record._id)
            .unwrap()
            .then(() => message.success(t('tradingDealHasBeenDeleted')))
            .catch((err) => message.error(getApiErrorMessage(err)))
      });
    },
    [deleteDeal, t]
  );

  const columns: ColumnsType<TradingDeal> = [
    {
      title: t('buyingPointName'),
      dataIndex: 'name',
      key: 'name',
      sorter: (a, b) => (a.name ?? '').localeCompare(b.name ?? ''),
      render: (val) => val ?? '—'
    },
    {
      title: t('rebatePercent'),
      dataIndex: 'percentage',
      key: 'percentage',
      sorter: (a, b) => (a.percentage ?? 0) - (b.percentage ?? 0),
      render: (val: number) => (val != null ? `${val}%` : '—')
    },
    {
      title: t('status'),
      dataIndex: 'isActive',
      key: 'isActive',
      render: (val: boolean) => (val ? t('active') : t('inactive'))
    },
    {
      /* Shown as a column rather than hidden in the edit dialog: which buying
         points book on a client order is the kind of thing worth being able to
         scan down a list to check. */
      title: t('requiresClientOrder'),
      dataIndex: 'requiresClientOrder',
      key: 'requiresClientOrder',
      render: (val: boolean) =>
        val ? <Tag color="gold">{t('clientOrderRequired')}</Tag> : <span>—</span>
    },
    {
      title: t('actions'),
      key: 'actions',
      width: 150,
      render: (_, record) => (
        <Space>
          <Button type="link" size="small" onClick={() => openEditModal(record)}>
            {t('edit')}
          </Button>
          <Button
            type="link"
            size="small"
            danger
            style={{ color: DELETE_ACTION_COLOR }}
            loading={isDeleting}
            onClick={() => handleDelete(record)}
          >
            {t('delete')}
          </Button>
        </Space>
      )
    }
  ];

  const deals = data?.tradingDeals ?? [];

  return (
    <>
      <Row style={{ marginBottom: 16 }}>
        <Button type="primary" onClick={openAddModal}>
          {t('addTradingDeal')}
        </Button>
      </Row>
      <Table<TradingDeal>
        columns={columns}
        dataSource={deals.map((d) => ({ ...d, key: d._id }))}
        loading={isLoading}
        pagination={false}
        locale={{ emptyText: t('noData') }}
      />
      <Modal
        title={editingDeal ? t('editTradingDeal') : t('addTradingDeal')}
        open={modalOpen}
        onOk={handleSubmit}
        onCancel={closeModal}
        confirmLoading={isCreating || isUpdating}
        destroyOnHidden
      >
        <Form form={form} layout="vertical" style={{ marginTop: 16 }}>
          <Form.Item
            name="name"
            label={t('buyingPointName')}
            rules={[{ required: true, message: t('requiredFieldMessage') }]}
          >
            <Input placeholder={t('buyingPointName')} />
          </Form.Item>
          <Form.Item
            name="percentage"
            label={t('rebatePercent')}
            rules={[{ required: true, message: t('requiredFieldMessage') }]}
          >
            <InputNumber<NumericInputValue>
              min={0}
              max={100}
              style={{ width: '100%' }}
              formatter={formatPercentInput}
              parser={parseNumericInput}
            />
          </Form.Item>
          {editingDeal && (
            <Form.Item name="isActive" label={t('status')} valuePropName="checked">
              <Switch checkedChildren={t('active')} unCheckedChildren={t('inactive')} />
            </Form.Item>
          )}

          {/* Set here rather than when raising an IO: the requirement belongs
              to the agency, so whoever raises one should not have to remember
              which buying points work this way. */}
          <Form.Item
            name="requiresClientOrder"
            label={t('requiresClientOrder')}
            valuePropName="checked"
            extra={t('requiresClientOrderHint')}
          >
            <Switch />
          </Form.Item>
        </Form>
      </Modal>
    </>
  );
};

const ChannelOverridesTab: FC = () => {
  const { t } = useTranslation(NAMESPACE.COMMON);
  const [form] = Form.useForm<{ name: string; channel: string; percentage: number }>();
  const [modalOpen, setModalOpen] = useState(false);
  const [editingRow, setEditingRow] = useState<TradingDealChannel | null>(null);

  const { data, isLoading } = useGetTradingDealChannelsQuery();
  const { data: dealsData } = useGetTradingDealsQuery({ includeInactive: true });
  const { data: channelsData } = useGetAdminChannelsQuery({ includeInactive: false });
  const [createRow, { isLoading: isCreating }] = useCreateAdminTradingDealChannelMutation();
  const [updateRow, { isLoading: isUpdating }] = useUpdateAdminTradingDealChannelMutation();
  const [deleteRow, { isLoading: isDeleting }] = useDeleteAdminTradingDealChannelMutation();

  const buyingPointOptions = useMemo(
    () =>
      [...new Set((dealsData?.tradingDeals ?? []).map((d) => d.name).filter(Boolean))]
        .sort((a, b) => a.localeCompare(b))
        .map((name) => ({ value: name, label: name })),
    [dealsData?.tradingDeals]
  );

  const channelOptions = useMemo(
    () =>
      (channelsData?.channels ?? [])
        .filter((c) => c.isActive)
        .map((c) => ({ value: c.label, label: c.label }))
        .sort((a, b) => a.label.localeCompare(b.label)),
    [channelsData?.channels]
  );

  const openAddModal = useCallback(() => {
    setEditingRow(null);
    form.resetFields();
    setModalOpen(true);
  }, [form]);

  const openEditModal = useCallback(
    (record: TradingDealChannel) => {
      setEditingRow(record);
      form.setFieldsValue({
        name: record.name,
        channel: record.channel,
        percentage: record.percentage
      });
      setModalOpen(true);
    },
    [form]
  );

  const closeModal = useCallback(() => {
    setModalOpen(false);
    setEditingRow(null);
    form.resetFields();
  }, [form]);

  const handleSubmit = useCallback(async () => {
    try {
      const values = await form.validateFields();
      if (editingRow) {
        await updateRow({
          id: editingRow._id,
          body: {
            name: values.name,
            channel: values.channel,
            percentage: values.percentage
          }
        }).unwrap();
        message.success(t('tradingDealChannelHasBeenEdited'));
      } else {
        await createRow({
          name: values.name,
          channel: values.channel,
          percentage: values.percentage
        }).unwrap();
        message.success(t('tradingDealChannelHasBeenCreated'));
      }
      closeModal();
    } catch (err) {
      message.error(getApiErrorMessage(err));
    }
  }, [closeModal, createRow, editingRow, form, t, updateRow]);

  const handleDelete = useCallback(
    (record: TradingDealChannel) => {
      confirm({
        title: <span style={{ color: DELETE_ACTION_COLOR }}>{t('deleteTradingDealChannelModalTitle')}</span>,
        content: t('deleteTradingDealChannelModalDescription'),
        icon: <ExclamationCircleOutlined />,
        okText: t('cancel'),
        cancelText: t('delete'),
        cancelButtonProps: { danger: true, style: { color: DELETE_ACTION_COLOR } },
        onCancel: () =>
          deleteRow(record._id)
            .unwrap()
            .then(() => message.success(t('tradingDealChannelHasBeenDeleted')))
            .catch((err) => message.error(getApiErrorMessage(err)))
      });
    },
    [deleteRow, t]
  );

  const columns: ColumnsType<TradingDealChannel> = [
    {
      title: t('buyingPointName'),
      dataIndex: 'name',
      key: 'name',
      sorter: (a, b) => (a.name ?? '').localeCompare(b.name ?? ''),
      render: (val) => val ?? '—'
    },
    {
      title: t('channel'),
      dataIndex: 'channel',
      key: 'channel',
      sorter: (a, b) => (a.channel ?? '').localeCompare(b.channel ?? ''),
      render: (val) => val ?? '—'
    },
    {
      title: t('rebatePercent'),
      dataIndex: 'percentage',
      key: 'percentage',
      sorter: (a, b) => (a.percentage ?? 0) - (b.percentage ?? 0),
      render: (val: number) => (val != null ? `${val}%` : '—')
    },
    {
      title: t('actions'),
      key: 'actions',
      width: 150,
      render: (_, record) => (
        <Space>
          <Button type="link" size="small" onClick={() => openEditModal(record)}>
            {t('edit')}
          </Button>
          <Button
            type="link"
            size="small"
            danger
            style={{ color: DELETE_ACTION_COLOR }}
            loading={isDeleting}
            onClick={() => handleDelete(record)}
          >
            {t('delete')}
          </Button>
        </Space>
      )
    }
  ];

  const rows = data?.tradingDealChannels ?? [];

  return (
    <>
      <Row style={{ marginBottom: 16 }}>
        <Button type="primary" onClick={openAddModal}>
          {t('addTradingDealChannel')}
        </Button>
      </Row>
      <Table<TradingDealChannel>
        columns={columns}
        dataSource={rows.map((r) => ({ ...r, key: r._id }))}
        loading={isLoading}
        pagination={false}
        locale={{ emptyText: t('noData') }}
      />
      <Modal
        title={editingRow ? t('editTradingDealChannel') : t('addTradingDealChannel')}
        open={modalOpen}
        onOk={handleSubmit}
        onCancel={closeModal}
        confirmLoading={isCreating || isUpdating}
        destroyOnHidden
      >
        <Form form={form} layout="vertical" style={{ marginTop: 16 }}>
          <Form.Item
            name="name"
            label={t('buyingPointName')}
            rules={[{ required: true, message: t('requiredFieldMessage') }]}
          >
            <Select
              showSearch
              placeholder={t('buyingPointName')}
              options={buyingPointOptions}
              optionFilterProp="label"
            />
          </Form.Item>
          <Form.Item
            name="channel"
            label={t('channel')}
            rules={[{ required: true, message: t('requiredFieldMessage') }]}
          >
            <Select
              showSearch
              placeholder={t('channel')}
              options={channelOptions}
              optionFilterProp="label"
            />
          </Form.Item>
          <Form.Item
            name="percentage"
            label={t('rebatePercent')}
            rules={[{ required: true, message: t('requiredFieldMessage') }]}
          >
            <InputNumber<NumericInputValue>
              min={0}
              max={100}
              style={{ width: '100%' }}
              formatter={formatPercentInput}
              parser={parseNumericInput}
            />
          </Form.Item>
        </Form>
      </Modal>
    </>
  );
};

const TraderControlsTradingDeals: FC = () => {
  const { t } = useTranslation(NAMESPACE.COMMON);

  const tabItems = useMemo(
    () => [
      {
        key: 'buying-points',
        label: t('tradingDealsBuyingPointsTab'),
        children: <BuyingPointsTab />
      },
      {
        key: 'channel-overrides',
        label: t('tradingDealsChannelOverridesTab'),
        children: <ChannelOverridesTab />
      }
    ],
    [t]
  );

  return (
    <>
      <Typography.Text strong style={{ display: 'block', marginBottom: 8, marginTop: 16, fontSize: 14 }}>
        {t('traderControlsTradingDeals')}
      </Typography.Text>
      <Typography.Paragraph type="secondary" style={{ marginBottom: 16, maxWidth: 720 }}>
        {t('traderControlsTradingDealsDescription')}
      </Typography.Paragraph>
      <Tabs items={tabItems} />
    </>
  );
};

export default TraderControlsTradingDeals;
